import { WaTemplateComponentRead, WaTemplateRead } from 'lib-trend-core';

/**
 * Which existing templates this builder is allowed to edit.
 *
 * The form builds Marketing → Standard and nothing else, so it can only edit a template it is able
 * to represent. The Graph API returns a wider world than that: carousel and limited-time-offer
 * components, GIF headers, and catalog / multi-product / single-product / flow / OTP buttons all
 * belong to template types this screen does not build. Opening one here would silently drop whatever
 * the form cannot hold — and saving would send that loss to Meta.
 *
 * The rule is derived from what the form can actually express rather than from `sub_category`, whose
 * values are not documented in the provider contract.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder — RF-32, RF-33.
 */

const SUPPORTED_COMPONENTS: ReadonlyArray<string> = ['HEADER', 'BODY', 'FOOTER', 'BUTTONS'];

/** GIF is deliberately absent: the media sample offers None/Image/Video/Document/Location. */
const SUPPORTED_HEADER_FORMATS: ReadonlyArray<string> = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'];

const SUPPORTED_BUTTONS: ReadonlyArray<string> = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE', 'VOICE_CALL'];

export function isTemplateEditable(template: WaTemplateRead): boolean {
  if (!template || template.category !== 'MARKETING') {
    return false;
  }

  const components = (template.components ?? []) as Array<WaTemplateComponentRead>;

  if (components.some((component) => SUPPORTED_COMPONENTS.indexOf(component.type) === -1)) {
    return false;
  }

  const header = components.find((component) => component.type === 'HEADER');
  if (!!header?.format && SUPPORTED_HEADER_FORMATS.indexOf(header.format) === -1) {
    return false;
  }

  const buttons = components.find((component) => component.type === 'BUTTONS')?.buttons ?? [];
  return !buttons.some((button) => SUPPORTED_BUTTONS.indexOf(button.type) === -1);
}
